import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { Modal, Button, Services, Text, TextInput, DateInput, NumberInput, Theme } from '@simplybenefits/sb-ui-library';
import Grid from "@mui/material/Grid";
import { Cancel as CancelIcon } from '@mui/icons-material';
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { validateToothNumber, validateToothSurface } from '../../../../util/dental.js';

class ProceduresModal extends PureComponent {
    state = {
        procedures: this.props.procedures ? this.props.procedures : []
    };

    _saveData = () => {
        this.props.saveData(this.state.procedures, 'procedures');
    };

    _validateForm = async () => {
        let isValid = true;

        const promises = this.state.procedures.map(async (procedure, index) => {
            const procedureCodeError = await Services.validateInput('checkEmpty', procedure.code);
            const serviceDateError = this.props.isPredetermination ? null : await Services.validateInput('birthdate', procedure.serviceDate);
            const dentistFeeClaimedError = await Services.validateInput('checkEmpty', procedure.dentistFeeClaimed);
            const toothSurfaceError = validateToothSurface(procedure.toothSurface);
            const toothNumberError = validateToothNumber(procedure.toothNumber);

            this.setState({
                [`procedureCodeError${ index }`]: procedureCodeError,
                [`serviceDateError${ index }`]: serviceDateError,
                [`dentistFeeClaimedError${ index }`]: dentistFeeClaimedError,
                [`toothSurfaceError${ index }`]: toothSurfaceError,
                [`toothNumberError${ index }`]: toothNumberError
            });

            if (
                procedureCodeError ||
                serviceDateError ||
                dentistFeeClaimedError ||
                toothSurfaceError ||
                toothNumberError
            ) {
                isValid = false;
                return false;
            }

            return true;
        });

        await Promise.all(promises);
        return isValid;
    };

    _next = async () => {
        const isValid = await this._validateForm();

        if (!isValid) {
            return;
        }

        await this._saveData();
        this.props.next();
    };

    _handleArrayItemChange = (type, index, key, e, isValue = false) => {
        this.setState({
            [type]: this.state[type].map((item, i) => {
                if (index !== i) {
                    return item;
                }

                return {
                    ...item,
                    [key]: isValue ? e : e.target.value
                };
            })
        });
    };

    _createProcedure = () => {
        const procedure = {
            code: '',
            isSubstitution: false,
            serviceDate: '',
            toothNumber: '',
            toothSurface: '',
            dentistFeeClaimed: '',
            labProcedureCode1: '',
            labProcedureCode2: '',
            labProcedureFee1: '',
            labProcedureFee2: '',
            typeCodes: '',
            dentistFeeAdjudicatedAmount: null,
            dentistFeeDeductible: null,
            labProcedureFee1AdjudicatedAmount: null,
            labProcedureFee1DeductibleAmount: null,
            labProcedureFee2AdjudicatedAmount: null,
            labProcedureFee2DeductibleAmount: null,
            overrideAdjudication: false,
            overrideNotes: ''
        };

        this.setState({
            procedures: [ ...this.state.procedures, procedure ]
        });
    };

    _removeProcedure = (index) => {
        this.setState({
            procedures: this.state.procedures.filter((item, i) => i !== index)
        });
    };

    render() {
        return (
            <Modal
                medium
                testid="submit-claim-modal"
                aria-labelledby="Submit Claim"
                open={true}
                title={ this.props.title }
                primaryButton={
                    this.state.procedures.length > 0
                        ? {
                            label: "Next",
                            onClick: this._next
                        }
                        : null
                }
                onClose={ this.props.onClose}
                loader={ this.props.loader }
                onBack={ this.props.onBack }
            >
                <Text mediumLarge>
                    Procedures
                </Text>

                {
                    this.state.procedures.map((procedure, index) => (
                        <div>
                            <Text medium bold color={ Theme.colors.skyBlue } margin="10px 0">
                                Procedure { index + 1 }

                                <CancelIcon
                                    onClick={ () => this._removeProcedure(index) }
                                    style={{ cursor: 'pointer', paddingTop: 10 }}
                                />
                            </Text>

                            <Grid
                                container
                                spacing={1}
                            >
                                <Grid item xs={12} sm={6} >
                                    <TextInput
                                        label="Procedure Code"
                                        variant="outlined"
                                        value={ procedure.code }
                                        onChange={ (e) => this._handleArrayItemChange('procedures', index, 'code', e) }
                                        required
                                        fullWidth
                                        error={Boolean(this.state[`procedureCodeError${ index }`])}
                                        helperText={this.state[`procedureCodeError${ index }`]}
                                        margin="0"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} >
                                    <DateInput
                                        label="Service Date"
                                        variant="outlined"
                                        value={ procedure.serviceDate }
                                        onChange={ (e) => this._handleArrayItemChange('procedures', index, 'serviceDate', e) }
                                        required
                                        fullWidth
                                        error={Boolean(this.state[`serviceDateError${ index }`])}
                                        helperText={this.state[`serviceDateError${ index }`]}
                                        margin="0"
                                        disabled={this.props.isPredetermination}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} >
                                    <TextInput
                                        label="Tooth Number"
                                        variant="outlined"
                                        value={ procedure.toothNumber }
                                        onChange={ (e) => this._handleArrayItemChange('procedures', index, 'toothNumber', e) }
                                        fullWidth
                                        error={Boolean(this.state[`toothNumberError${ index }`])}
                                        helperText={ this.state[`toothNumberError${ index }`] }
                                        margin="0"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} >
                                    <TextInput
                                        label="Tooth Surface"
                                        variant="outlined"
                                        value={ procedure.toothSurface }
                                        onChange={ (e) => this._handleArrayItemChange('procedures', index, 'toothSurface', e) }
                                        fullWidth
                                        error={Boolean(this.state[`toothSurfaceError${ index }`])}
                                        helperText={ this.state[`toothSurfaceError${ index }`]}
                                        margin="0"
                                    />
                                </Grid>

                                <Grid item xs={12} >
                                    <NumberInput
                                        prefix="$"
                                        label="Dentist Fee Claimed"
                                        variant="outlined"
                                        value={ procedure.dentistFeeClaimed }
                                        onChange={ (e) => this._handleArrayItemChange('procedures', index, 'dentistFeeClaimed', e) }
                                        required
                                        fullWidth
                                        error={Boolean(this.state[`dentistFeeClaimedError${ index }`])}
                                        helperText={this.state[`dentistFeeClaimedError${ index }`]}
                                        margin="0"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} >
                                    <TextInput
                                        label="Lab Code 1"
                                        variant="outlined"
                                        value={ procedure.labProcedureCode1 }
                                        onChange={ (e) => this._handleArrayItemChange('procedures', index, 'labProcedureCode1', e) }
                                        fullWidth
                                        error={Boolean(this.state[`labProcedureCode1Error${ index }`])}
                                        helperText={this.state[`labProcedureCode1Error${ index }`]}
                                        margin="0"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} >
                                    <NumberInput
                                        prefix="$"
                                        label="Lab Fee 1"
                                        variant="outlined"
                                        value={ procedure.labProcedureFee1 }
                                        onChange={ (e) => this._handleArrayItemChange('procedures', index, 'labProcedureFee1', e) }
                                        fullWidth
                                        error={Boolean(this.state[`labProcedureFee1Error${ index }`])}
                                        helperText={this.state[`labProcedureFee1Error${ index }`]}
                                        margin="0"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} >
                                    <TextInput
                                        label="Lab Code 2"
                                        variant="outlined"
                                        value={ procedure.labProcedureCode2 }
                                        onChange={ (e) => this._handleArrayItemChange('procedures', index, 'labProcedureCode2', e) }
                                        fullWidth
                                        error={Boolean(this.state[`labProcedureCode2Error${ index }`])}
                                        helperText={this.state[`labProcedureCode2Error${ index }`]}
                                        margin="0"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} >
                                    <NumberInput
                                        prefix="$"
                                        label="Lab Fee 2"
                                        variant="outlined"
                                        value={ procedure.labProcedureFee2 }
                                        onChange={ (e) => this._handleArrayItemChange('procedures', index, 'labProcedureFee2', e) }
                                        fullWidth
                                        error={Boolean(this.state[`labProcedureFee2Error${ index }`])}
                                        helperText={this.state[`labProcedureFee2Error${ index }`]}
                                        margin="0"
                                    />
                                </Grid>

                                <Grid item xs={12} >
                                    <TextInput
                                        label="Type Codes"
                                        variant="outlined"
                                        value={ procedure.typeCodes }
                                        onChange={ (e) => this._handleArrayItemChange('procedures', index, 'typeCodes', e) }
                                        fullWidth
                                        error={Boolean(this.state[`typeCodesError${ index }`])}
                                        helperText={this.state[`typeCodesError${ index }`]}
                                        margin="0"
                                    />
                                </Grid>
                            </Grid>

                            <Text small bold color={ Theme.colors.skyBlue } margin="10px 0">
                                    Use the button below to override the adjudication amounts the system will compute. Please note the system will use whatever you enter here. This functionality should only be used in rare cases.
                            </Text>

                            {
                                !procedure.overrideAdjudication ?
                                    <Button
                                        color="primary"
                                        onClick={ () => this._handleArrayItemChange('procedures', index, 'overrideAdjudication', true, true) }
                                        customStyle={{ margin: "10px auto 20px auto", display: 'block' }}
                                    >
                                            OVERRIDE SYSTEM ADJUDICATION
                                    </Button>
                                    :
                                    <Button
                                        color="primary"
                                        onClick={ () => this._handleArrayItemChange('procedures', index, 'overrideAdjudication', false, true) }
                                        customStyle={{ margin: "10px auto 20px auto", display: 'block' }}
                                    >
                                            CANCEL OVERRIDE
                                    </Button>
                            }

                            <Grid
                                container
                                spacing={1}
                            >
                                {
                                    procedure.overrideAdjudication &&
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={ procedure.isSubstitution }
                                                        onChange={ () => this._handleArrayItemChange('procedures', index, 'isSubstitution', !procedure.isSubstitution, true) }
                                                        value="isSubstitution"
                                                    />
                                                }
                                                label="This is a substitution code for hygienists and/or denturists."
                                            />
                                }

                                <Grid item xs={12} sm={6} >
                                    <NumberInput
                                        prefix="$"
                                        label="Dentist Fee Adjudicated Amount"
                                        variant="outlined"
                                        value={ procedure.dentistFeeAdjudicatedAmount }
                                        onChange={ (e) => this._handleArrayItemChange('procedures', index, 'dentistFeeAdjudicatedAmount', e) }
                                        required
                                        fullWidth
                                        error={Boolean(this.state[`dentistFeeAdjudicatedAmountError${ index }`])}
                                        helperText={this.state[`dentistFeeAdjudicatedAmountError${ index }`]}
                                        margin="0"
                                        disabled={ !procedure.overrideAdjudication }
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} >
                                    <NumberInput
                                        prefix="$"
                                        label="Dentist Fee Deductible"
                                        variant="outlined"
                                        value={ procedure.dentistFeeDeductible }
                                        onChange={ (e) => this._handleArrayItemChange('procedures', index, 'dentistFeeDeductible', e) }
                                        required
                                        fullWidth
                                        error={Boolean(this.state[`dentistFeeDeductibleError${ index }`])}
                                        helperText={this.state[`dentistFeeDeductibleError${ index }`]}
                                        margin="0"
                                        disabled={ !procedure.overrideAdjudication }
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} >
                                    <NumberInput
                                        prefix="$"
                                        label="Lab Fee 1 Adjudicated Amount"
                                        variant="outlined"
                                        value={ procedure.labProcedureFee1AdjudicatedAmount }
                                        onChange={ (e) => this._handleArrayItemChange('procedures', index, 'labProcedureFee1AdjudicatedAmount', e) }
                                        fullWidth
                                        error={Boolean(this.state[`labProcedureFee1AdjudicatedAmountError${ index }`])}
                                        helperText={this.state[`labProcedureFee1AdjudicatedAmountError${ index }`]}
                                        margin="0"
                                        disabled={ !procedure.overrideAdjudication }
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} >
                                    <NumberInput
                                        prefix="$"
                                        label="Lab Fee 1 Deductible Amount"
                                        variant="outlined"
                                        value={ procedure.labProcedureFee1DeductibleAmount }
                                        onChange={ (e) => this._handleArrayItemChange('procedures', index, 'labProcedureFee1DeductibleAmount', e) }
                                        fullWidth
                                        error={Boolean(this.state[`labProcedureFee1DeductibleAmountError${ index }`])}
                                        helperText={this.state[`labProcedureFee1DeductibleAmountError${ index }`]}
                                        margin="0"
                                        disabled={ !procedure.overrideAdjudication }
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} >
                                    <NumberInput
                                        prefix="$"
                                        label="Lab Fee 2 Adjudicated Amount"
                                        variant="outlined"
                                        value={ procedure.labProcedureFee2AdjudicatedAmount }
                                        onChange={ (e) => this._handleArrayItemChange('procedures', index, 'labProcedureFee2AdjudicatedAmount', e) }
                                        fullWidth
                                        error={Boolean(this.state[`labProcedureFee2AdjudicatedAmountError${ index }`])}
                                        helperText={this.state[`labProcedureFee2AdjudicatedAmountError${ index }`]}
                                        margin="0"
                                        disabled={ !procedure.overrideAdjudication }
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} >
                                    <NumberInput
                                        prefix="$"
                                        label="Lab Fee 2 Deductible Amount"
                                        variant="outlined"
                                        value={ procedure.labProcedureFee2DeductibleAmount }
                                        onChange={ (e) => this._handleArrayItemChange('procedures', index, 'labProcedureFee2DeductibleAmount', e) }
                                        fullWidth
                                        error={Boolean(this.state[`labProcedureFee2DeductibleAmountError${ index }`])}
                                        helperText={this.state[`labProcedureFee2DeductibleAmountError${ index }`]}
                                        margin="0"
                                        disabled={ !procedure.overrideAdjudication }
                                    />
                                </Grid>

                                <Grid item xs={12} >
                                    <TextInput
                                        label="Notes"
                                        variant="outlined"
                                        rows={5}
                                        rowsMax={10}
                                        multiline={true}
                                        value={ procedure.overrideNotes }
                                        onChange={ (e) => this._handleArrayItemChange('procedures', index, 'overrideNotes', e) }
                                        required
                                        fullWidth
                                        margin="0"
                                        disabled={ !procedure.overrideAdjudication }
                                        helperText={<Text avMedium tiny>Separate each note with a newline. Each note can be maximum 75 characters.</Text>}
                                    />
                                </Grid>
                            </Grid>
                        </div>
                    ))
                }

                {
                    this.state.procedures.length < 7 &&
                            <Button
                                onClick={this._createProcedure}
                                transparent
                                customStyle={{ margin: "10px 0 20px 0", width: "100%" }}
                            >
                                ADD PROCEDURE
                            </Button>
                }
            </Modal>
        );
    }
}

export default connect()(ProceduresModal);